import React, { useEffect, useMemo, useState } from 'react';
import { arrayUnion, collection, doc, getDoc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import Spinner from './Spinner.js';
import MultiFileUpload from './components/MultiFileUpload.js';
import { getDownloadURL, ref, storage, uploadBytes } from './firebaseUtils.js';

const getToday = () => new Date().toISOString().slice(0, 10);
const createExpenseLine = () => ({ expenseDate: '', description: '', documentNo: '', amount: '' });
const formatAmount = (amount) => Number(amount || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR' });

const CreateAdvanceSettlement = ({ db, user }) => {
  const navigate = useNavigate();
  const { requestId, settlementId } = useParams();
  const isEditMode = Boolean(settlementId);
  const [advance, setAdvance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expenseLines, setExpenseLines] = useState([createExpenseLine()]);
  const [attachments, setAttachments] = useState([]);
  const [settlementDate, setSettlementDate] = useState(getToday());
  const [remarks, setRemarks] = useState('');
  const [declarationAccepted, setDeclarationAccepted] = useState(false);

  useEffect(() => {
    const loadSettlement = async () => {
      try {
        const advanceSnapshot = await getDoc(doc(db, 'payment_requests', requestId));
        if (!advanceSnapshot.exists()) throw new Error('Advance request not found.');

        const advanceData = { id: advanceSnapshot.id, ...advanceSnapshot.data() };
        if (advanceData.requestType !== 'Employee Advance') throw new Error('Settlement is available only for employee advances.');
        if (advanceData.requesterUid !== user?.uid) throw new Error('Only the employee who received this advance can create its settlement.');

        setAdvance(advanceData);

        if (isEditMode) {
          const settlementSnapshot = await getDoc(doc(db, 'payment_request_settlements', settlementId));
          if (!settlementSnapshot.exists()) throw new Error('Advance settlement not found.');
          const settlement = settlementSnapshot.data();
          if (settlement.advanceRequestId !== requestId || settlement.employeeUid !== user?.uid) {
            throw new Error('You do not have access to this settlement.');
          }
          if (!['Draft', 'ReturnedBySettlementManager', 'ReturnedBySettlementAccounts', 'ReturnedBySettlementHR'].includes(settlement.status)) {
            throw new Error('This settlement can no longer be edited.');
          }

          setExpenseLines(settlement.expenseLines?.length
            ? settlement.expenseLines.map((line) => ({ ...createExpenseLine(), ...line }))
            : [createExpenseLine()]);
          setAttachments(Array.isArray(settlement.attachments) ? settlement.attachments : []);
          setSettlementDate(settlement.settlementDate || getToday());
          setRemarks(settlement.remarks || '');
          setDeclarationAccepted(Boolean(settlement.declarationAccepted));
        } else if (advanceData.activeSettlementId) {
          navigate(`/payment-requests/${requestId}/settlements/${advanceData.activeSettlementId}/edit`, { replace: true });
        }
      } catch (loadError) {
        console.error('Unable to load advance settlement:', loadError);
        toast.error(loadError.message || 'Unable to load the advance settlement.');
        navigate(`/payment-requests/${requestId}`);
      } finally {
        setLoading(false);
      }
    };

    loadSettlement();
  }, [db, isEditMode, navigate, requestId, settlementId, user?.uid]);

  const advanceReceivedAmount = useMemo(() => {
    if (!advance) return 0;
    const payments = Array.isArray(advance.payments) ? advance.payments : [];
    const totalNetPaid = payments.reduce((total, payment) => total + Number(payment?.netPayment || 0), 0);
    return totalNetPaid || Number(advance.totalNetPaid || advance.totalSettledAmount || 0);
  }, [advance]);
  const declaredExpenseAmount = useMemo(() => expenseLines.reduce((total, line) => total + (Number(line.amount) || 0), 0), [expenseLines]);
  const settlementBalance = advanceReceivedAmount - declaredExpenseAmount;
  const balanceLabel = settlementBalance === 0
    ? 'Settlement balance'
    : settlementBalance > 0 ? 'Balance to return / adjust' : 'Additional amount claimed';

  const updateExpenseLine = (index, field, value) => {
    setExpenseLines((current) => current.map((line, lineIndex) => (
      lineIndex === index ? { ...line, [field]: value } : line
    )));
  };

  const uploadAttachments = async (targetSettlementId) => Promise.all(attachments.map(async (attachment, index) => {
    if (attachment.url) {
      return {
        name: attachment.name || `Attachment ${index + 1}`,
        size: attachment.size || 0,
        type: attachment.type || '',
        url: attachment.url,
        storagePath: attachment.storagePath || ''
      };
    }

    const file = attachment.file || attachment;
    const safeName = String(file.name || `attachment-${index + 1}`).replace(/[^a-zA-Z0-9._-]/g, '_');
    const storagePath = `payment_request_settlements/${targetSettlementId}/attachments/${Date.now()}_${index}_${safeName}`;
    const storageRef = ref(storage, storagePath);
    await uploadBytes(storageRef, file, { contentType: file.type || 'application/octet-stream' });
    return {
      name: file.name || `Attachment ${index + 1}`,
      size: file.size || 0,
      type: file.type || '',
      url: await getDownloadURL(storageRef),
      storagePath
    };
  }));

  const saveSettlement = async (shouldSubmit) => {
    const normalizedLines = expenseLines.map((line) => ({
      expenseDate: line.expenseDate,
      description: line.description.trim(),
      documentNo: line.documentNo.trim(),
      amount: Number(line.amount) || 0
    }));

    if (shouldSubmit) {
      if (!declarationAccepted) return toast.error('Confirm the business-expense declaration before submitting.');
      if (!normalizedLines.length || normalizedLines.some((line) => !line.expenseDate || !line.description || line.amount <= 0)) {
        return toast.error('Complete the date, description, and amount for every expense line.');
      }
    }

    setSaving(true);
    try {
      const targetSettlementId = settlementId || doc(collection(db, 'payment_request_settlements')).id;
      const settlementRef = doc(db, 'payment_request_settlements', targetSettlementId);
      const advanceRef = doc(db, 'payment_requests', requestId);
      const settlementYear = Number.parseInt(String(settlementDate || getToday()).slice(0, 4), 10) || new Date().getFullYear();
      const counterRef = doc(db, 'payment_request_settlement_counters', String(settlementYear));
      const savedAttachments = await uploadAttachments(targetSettlementId);
      const status = shouldSubmit
        ? (advance.managerEmail ? 'Waiting Manager Settlement Review' : 'Waiting Accounts Settlement Check')
        : 'Draft';
      const savedAt = new Date().toISOString();
      const balanceDirection = settlementBalance === 0
        ? 'none'
        : settlementBalance > 0 ? 'recover_from_employee' : 'payable_to_employee';
      const settlementPayload = {
        advanceRequestId: requestId,
        advanceRequestNumber: advance.requestNumber || requestId,
        employeeUid: user?.uid || '',
        employeeEmail: user?.email || '',
        employeeName: user?.displayName || user?.email || '',
        managerEmail: advance.managerEmail || '',
        managerName: advance.managerName || '',
        advanceReceivedAmount,
        settlementDate,
        expenseLines: normalizedLines,
        declaredExpenseAmount,
        balanceAmount: Math.abs(settlementBalance),
        balanceDirection,
        remarks: remarks.trim(),
        declarationAccepted,
        attachments: savedAttachments,
        status,
        updatedAt: serverTimestamp()
      };

      await runTransaction(db, async (transaction) => {
        const advanceSnapshot = await transaction.get(advanceRef);
        if (!advanceSnapshot.exists()) throw new Error('Advance request not found.');
        const latestAdvance = advanceSnapshot.data();
        if (latestAdvance.requesterUid !== user?.uid || latestAdvance.requestType !== 'Employee Advance') {
          throw new Error('You do not have access to this advance.');
        }
        if (!isEditMode && latestAdvance.activeSettlementId && latestAdvance.activeSettlementId !== targetSettlementId) {
          throw new Error('An active settlement already exists for this advance.');
        }

        let settlementNumber = '';
        if (!isEditMode) {
          const counterSnapshot = await transaction.get(counterRef);
          const nextSequence = (counterSnapshot.data()?.lastSequence || 0) + 1;
          settlementNumber = `PRS/${settlementYear}/${String(nextSequence).padStart(4, '0')}`;
          transaction.set(counterRef, { lastSequence: nextSequence, updatedAt: serverTimestamp() }, { merge: true });
        }

        transaction.set(settlementRef, {
          ...settlementPayload,
          ...(settlementNumber ? { settlementNumber } : {}),
          ...(isEditMode ? {} : {
            createdAt: serverTimestamp(),
            auditTrail: [{
              action: shouldSubmit ? 'submitted_for_settlement_review' : 'saved_draft',
              at: savedAt,
              byUid: user?.uid || '',
              byName: user?.displayName || user?.email || ''
            }]
          }),
          ...(isEditMode ? {
            auditTrail: arrayUnion({
              action: shouldSubmit ? 'submitted_for_settlement_review' : 'saved_draft',
              at: savedAt,
              byUid: user?.uid || '',
              byName: user?.displayName || user?.email || ''
            })
          } : {})
        }, { merge: true });
        transaction.update(advanceRef, {
          activeSettlementId: targetSettlementId,
          settlementStatus: status,
          settlementClaimedAmount: declaredExpenseAmount,
          settlementOutstandingAmount: Math.abs(settlementBalance),
          status: shouldSubmit ? 'Settlement Under Review' : 'Advance Paid Awaiting Settlement',
          ...(shouldSubmit ? { settlementSubmittedAt: serverTimestamp() } : {}),
          updatedAt: serverTimestamp(),
          auditTrail: arrayUnion({
            action: shouldSubmit ? 'settlement_submitted' : 'settlement_draft_saved',
            at: savedAt,
            byUid: user?.uid || '',
            byName: user?.displayName || user?.email || '',
            settlementId: targetSettlementId
          })
        });
      });

      toast.success(shouldSubmit ? 'Advance settlement submitted.' : 'Settlement draft saved.');
      navigate(`/payment-requests/${requestId}`);
    } catch (saveError) {
      console.error('Unable to save advance settlement:', saveError);
      toast.error(saveError.message || 'Unable to save the advance settlement. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = { width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '7px', fontSize: '14px', color: '#0f172a', background: '#fff' };
  const labelStyle = { display: 'block', marginBottom: '6px', color: '#334155', fontSize: '13px', fontWeight: '700' };
  const sectionStyle = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '22px', marginBottom: '20px' };

  if (loading) return <Spinner />;
  if (!advance) return null;

  return (
    <div style={{ padding: '24px', maxWidth: '1050px', margin: '0 auto' }}>
      <button type="button" onClick={() => navigate(`/payment-requests/${requestId}`)} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', border: 0, padding: 0, marginBottom: '18px', color: '#2563eb', background: 'transparent', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}><ArrowLeft size={17} /> Back to Advance Request</button>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', marginBottom: '24px' }}>
        <div>
          <h2 style={{ margin: 0, color: '#0f172a' }}>{isEditMode ? 'Continue Advance Settlement' : 'New Advance Settlement'}</h2>
          <p style={{ margin: '6px 0 0', color: '#64748b' }}>Declare the expenses made from the advance you received.</p>
        </div>
        <div style={{ display: 'flex', gap: '12px', flexShrink: 0 }}>
          <button type="button" disabled={saving} onClick={() => saveSettlement(false)} style={{ padding: '11px 18px', border: '1px solid #64748b', borderRadius: '7px', background: '#fff', color: '#334155', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: '700' }}>Save Draft</button>
          <button type="button" disabled={saving} onClick={() => saveSettlement(true)} style={{ padding: '11px 18px', border: 0, borderRadius: '7px', background: '#2563eb', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: '700' }}>{saving ? 'Saving…' : 'Submit Settlement'}</button>
        </div>
      </div>

      <section style={{ ...sectionStyle, background: '#f8fafc' }}>
        <h3 style={{ margin: '0 0 16px', color: '#1e293b', fontSize: '16px' }}>Linked Advance</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '16px', color: '#334155', fontSize: '14px' }}>
          <div><strong style={{ display: 'block', color: '#64748b', fontSize: '12px', marginBottom: '4px' }}>ADVANCE REQUEST</strong>{advance.requestNumber || requestId}</div>
          <div><strong style={{ display: 'block', color: '#64748b', fontSize: '12px', marginBottom: '4px' }}>EMPLOYEE</strong>{advance.requesterName || user?.displayName || user?.email}</div>
          <div><strong style={{ display: 'block', color: '#64748b', fontSize: '12px', marginBottom: '4px' }}>ADVANCE RECEIVED</strong>{formatAmount(advanceReceivedAmount)}</div>
          <div><strong style={{ display: 'block', color: '#64748b', fontSize: '12px', marginBottom: '4px' }}>PURPOSE</strong>{advance.paymentAdvanceRemarks || '—'}</div>
        </div>
      </section>

      <section style={sectionStyle}>
        <h3 style={{ margin: '0 0 18px', color: '#1e293b', fontSize: '16px' }}>Expense Details</h3>
        <label style={{ ...labelStyle, maxWidth: '260px' }}>Settlement Date<input type="date" value={settlementDate} onChange={(event) => setSettlementDate(event.target.value)} style={inputStyle} /></label>
        <div style={{ overflowX: 'auto', marginTop: '18px' }}>
          <table style={{ width: '100%', minWidth: '760px', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <colgroup><col style={{ width: '17%' }} /><col style={{ width: '48%' }} /><col style={{ width: '20%' }} /><col style={{ width: '15%' }} /><col style={{ width: '38px' }} /></colgroup>
            <thead><tr style={{ background: '#f8fafc' }}>{['Expense Date', 'Description', 'Bill / Reference No.', 'Amount', ''].map((heading) => <th key={heading || 'actions'} style={{ padding: '10px', textAlign: 'left', fontSize: '12px', color: '#475569', borderBottom: '1px solid #e2e8f0' }}>{heading}</th>)}</tr></thead>
            <tbody>{expenseLines.map((line, index) => <tr key={index}>
              <td style={{ padding: '10px 6px 10px 0' }}><input type="date" value={line.expenseDate} onChange={(event) => updateExpenseLine(index, 'expenseDate', event.target.value)} style={inputStyle} /></td>
              <td style={{ padding: '10px 6px' }}><textarea rows="1" value={line.description} onChange={(event) => updateExpenseLine(index, 'description', event.target.value)} placeholder="Describe the expense" style={{ ...inputStyle, height: '40px', minHeight: '40px', resize: 'vertical' }} /></td>
              <td style={{ padding: '10px 6px' }}><input value={line.documentNo} onChange={(event) => updateExpenseLine(index, 'documentNo', event.target.value)} placeholder="Optional" style={inputStyle} /></td>
              <td style={{ padding: '10px 6px' }}><input type="number" min="0" step="0.01" value={line.amount} onChange={(event) => updateExpenseLine(index, 'amount', event.target.value)} style={inputStyle} /></td>
              <td style={{ padding: '10px 0 10px 6px', width: '38px' }}><button type="button" aria-label="Remove expense line" disabled={expenseLines.length === 1} onClick={() => setExpenseLines((current) => current.filter((_, lineIndex) => lineIndex !== index))} style={{ border: 0, color: expenseLines.length === 1 ? '#cbd5e1' : '#dc2626', background: 'transparent', cursor: expenseLines.length === 1 ? 'not-allowed' : 'pointer' }}><Trash2 size={18} /></button></td>
            </tr>)}</tbody>
          </table>
        </div>
        <button type="button" onClick={() => setExpenseLines((current) => [...current, createExpenseLine()])} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginTop: '12px', padding: '8px 12px', border: '1px solid #2563eb', borderRadius: '7px', color: '#2563eb', background: '#fff', cursor: 'pointer', fontWeight: '700' }}><Plus size={16} /> Add Expense</button>
        <div style={{ maxWidth: '430px', margin: '24px 0 0 auto', borderTop: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', color: '#334155' }}><span>Advance Received</span><strong>{formatAmount(advanceReceivedAmount)}</strong></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', color: '#334155' }}><span>Expenses Declared</span><strong>{formatAmount(declaredExpenseAmount)}</strong></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderTop: '1px solid #e2e8f0', color: '#0f172a', fontSize: '16px' }}><strong>{balanceLabel}</strong><strong>{formatAmount(Math.abs(settlementBalance))}</strong></div>
        </div>
      </section>

      <section style={sectionStyle}>
        <h3 style={{ margin: '0 0 6px', color: '#1e293b', fontSize: '16px' }}>Supporting Documents</h3>
        <p style={{ margin: '0 0 18px', color: '#64748b', fontSize: '13px' }}>Upload bills, invoices, or other proof for the expenses declared above.</p>
        <MultiFileUpload value={attachments} onFilesChange={setAttachments} maxSizeMB={10} helperText="JPG, PNG, or PDF files only. Maximum total size: 10MB." disabled={saving} uploading={saving} />
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginTop: '18px', color: '#334155', fontSize: '14px', lineHeight: 1.5, cursor: 'pointer' }}>
          <input type="checkbox" checked={declarationAccepted} onChange={(event) => setDeclarationAccepted(event.target.checked)} disabled={saving} style={{ width: '16px', height: '16px', marginTop: '3px', accentColor: '#2563eb' }} />
          <span>I confirm these expenses were incurred for business, have not been claimed or paid elsewhere, and the details and evidence are correct.</span>
        </label>
      </section>

      <section style={sectionStyle}>
        <label style={labelStyle}>Settlement Remarks<textarea rows="3" value={remarks} onChange={(event) => setRemarks(event.target.value)} placeholder="Add anything Accounts should know about this settlement" style={{ ...inputStyle, resize: 'vertical' }} /></label>
      </section>
    </div>
  );
};

export default CreateAdvanceSettlement;
